"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import { ArgumentMapDiagram } from "@/components/tools/diagrams-more/argument-map-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  ARGUMENT_LIMIT,
  type ArgumentDirection,
  argumentCounts,
  argumentOutline,
  argumentRows,
  layoutArgumentMap,
  parseArgumentMap,
} from "@/lib/tool-kit/diagrams-more/argument-map";

/** English strings. A translated content file overrides these through `ui`. */
export const ARGUMENT_MAP_MAKER_STRINGS = {
  inputLabel:
    "Your argument — the main claim first, then reasons and objections, indented",
  inputHint:
    "Line one is the main claim. Under a claim, write Because: (or +) for a reason and But: (or -) for an objection, indented one step deeper. A reason can have reasons and objections of its own, and an objection to an objection is a rebuttal. A line with no marker is read as a reason.",
  exampleText:
    "Claim: Universities should make lecture attendance optional\nBecause: Students learn in different ways\n  Because: Recorded lectures can be paused and replayed\n  Because: Some students prefer to read before class\n  But: Some students need the routine of a timetable\n    But: Optional attendance leaves the timetable in place\nBecause: Compulsory registers use up staff time\nBut: Lectures are where students ask questions\n  But: Questions can go to online forums and office hours",
  exampleTitle: "Should Lecture Attendance Be Optional?",
  loadExample: "Load the example",
  clear: "Clear",
  directionLabel: "Direction",
  directionDown: "Top to bottom",
  directionRight: "Left to right",
  tagClaim: "Claim",
  tagReason: "Reason",
  tagObjection: "Objection",
  tagRebuttal: "Rebuttal",
  boxesDetected: "one:{count} box|other:{count} boxes",
  reasonsDetected: "one:{count} reason|other:{count} reasons",
  objectionsDetected:
    "one:{count} objection or rebuttal|other:{count} objections and rebuttals",
  unmarkedNotice:
    "one:{count} line had no marker and was read as a reason. Start it with But: if it is an objection.|other:{count} lines had no marker and were read as reasons. Start a line with But: if it is an objection.",
  enterClaim: "Type the main claim on the first line to start the map.",
  dropped:
    "one:{count} line was left out — a map holds up to {limit} boxes.|other:{count} lines were left out — a map holds up to {limit} boxes.",
  numberColumn: "No.",
  roleColumn: "Role",
  statementColumn: "Statement",
  respondsColumn: "Bears on",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title (optional)",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy as outline",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the map as a numbered table",
  chartLabel: "Argument map",
  chartDescription:
    "Argument map of the claim “{claim}” with {reasons} and {objections}. Reasons are solid green boxes with solid arrows; objections and rebuttals are dashed vermillion boxes with dashed arrows.",
} as const;

export default function ArgumentMapMakerWidget() {
  const strings = useToolUi(ARGUMENT_MAP_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleText);
  const [direction, setDirection] = useState<ArgumentDirection>("down");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parse = parseArgumentMap(raw);
  const layout =
    parse.nodes.length > 0 ? layoutArgumentMap(parse, direction) : null;
  const counts = argumentCounts(parse);
  const tags = {
    claim: strings.tagClaim,
    reason: strings.tagReason,
    objection: strings.tagObjection,
    rebuttal: strings.tagRebuttal,
  };
  const against = counts.objection + counts.rebuttal;
  const boxesText = tPlural(strings.boxesDetected, locale, parse.nodes.length, {
    count: fmt(parse.nodes.length),
  });
  const reasonsText = tPlural(strings.reasonsDetected, locale, counts.reason, {
    count: fmt(counts.reason),
  });
  const objectionsText = tPlural(strings.objectionsDetected, locale, against, {
    count: fmt(against),
  });
  const rows = argumentRows(parse).map((row) => [
    row.number,
    tags[row.kind],
    row.text,
    row.respondsTo,
  ]);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="amm-input"
        label={strings.inputLabel}
        onChange={setRaw}
        rows={12}
        value={raw}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw(strings.exampleText)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setRaw("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-2">
        {layout ? (
          <p className={HINT}>
            {boxesText}
            {" · "}
            {reasonsText}
            {" · "}
            {objectionsText}
          </p>
        ) : (
          <Notice>{strings.enterClaim}</Notice>
        )}
        {parse.unmarked > 0 ? (
          <Notice>
            {tPlural(strings.unmarkedNotice, locale, parse.unmarked, {
              count: fmt(parse.unmarked),
            })}
          </Notice>
        ) : null}
        {parse.dropped > 0 ? (
          <Notice>
            {tPlural(strings.dropped, locale, parse.dropped, {
              count: fmt(parse.dropped),
              limit: fmt(ARGUMENT_LIMIT),
            })}
          </Notice>
        ) : null}
      </div>
      <PillGroup
        label={strings.directionLabel}
        name="amm-direction"
        onChange={setDirection}
        options={[
          { value: "down", label: strings.directionDown },
          { value: "right", label: strings.directionRight },
        ]}
        value={direction}
      />
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={argumentOutline(parse)}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "argument-map")}
          labels={strings}
          note={style.note}
          table={{
            head: [
              strings.numberColumn,
              strings.roleColumn,
              strings.statementColumn,
              strings.respondsColumn,
            ],
            rows,
          }}
          title={style.title}
        >
          <ArgumentMapDiagram
            description={t(strings.chartDescription, {
              claim: parse.nodes[parse.roots[0]].text,
              reasons: reasonsText,
              objections: objectionsText,
            })}
            id="amm-chart"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
            tags={tags}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="amm"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
